import { Box, Stack, Text, useMantineTheme } from '@mantine/core';
import { Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { getAnnualPaceStatus } from '../../statusUtils';
import type { TuAswpData } from '../types';
import { latest, pctChange, previous } from '../helpers';
import { CardShell } from './CardShell';
import { MetricRow } from './MetricRow';

type HighSchoolCardProps = { data: TuAswpData };

export function HighSchoolCard({ data }: HighSchoolCardProps) {
  const theme = useMantineTheme();
  const series = data.highSchool;
  const cur = series.length > 0 ? latest(series) : null;
  const prev = previous(series);

  const goalPerYear = 25;
  const goalPerQuarter = 7;

  const parseQuarterLabel = (label: string): { q: number; y: number } => {
    // Expected format: "Q1 2026"
    const q = Number(label.slice(1, 2));
    const y = Number(label.split(' ')[1]);
    return { q: Number.isFinite(q) ? q : 1, y: Number.isFinite(y) ? y : new Date().getFullYear() };
  };

  const curQ = parseQuarterLabel(cur?.quarter ?? '');
  const engagedYtd = series.reduce((acc, p) => {
    const qp = parseQuarterLabel(p.quarter);
    return acc + (qp.y === curQ.y && qp.q <= curQ.q ? p.highSchoolEngaged : 0);
  }, 0);
  const engagedYtdPrevYear = series.reduce((acc, p) => {
    const qp = parseQuarterLabel(p.quarter);
    return acc + (qp.y === curQ.y - 1 && qp.q <= curQ.q ? p.highSchoolEngaged : 0);
  }, 0);
  const qoq = cur ? pctChange(cur.highSchoolEngaged, prev?.highSchoolEngaged ?? null) : null;
  const yoy = engagedYtdPrevYear > 0 ? ((engagedYtd - engagedYtdPrevYear) / engagedYtdPrevYear) * 100 : null;
  const arrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  const qoqText = qoq === null ? '—' : arrow(qoq) ? `${arrow(qoq)}${Math.abs(qoq).toFixed(2)}% QoQ` : '0.00% QoQ';
  const yoyText =
    yoy === null ? '— YoY' : arrow(yoy) ? `${arrow(yoy)}${Math.abs(yoy).toFixed(2)}% YoY` : '0.00% YoY';

  const status = cur
    ? getAnnualPaceStatus({ ytd: engagedYtd, goalPerYear, quarterLabel: cur.quarter, direction: 'min' })
    : { label: 'Snapshot' as const, color: 'gray' as const };

  const annualTotals = series.reduce<Record<number, number>>((acc, point) => {
    const qp = parseQuarterLabel(point.quarter);
    acc[qp.y] = (acc[qp.y] ?? 0) + point.highSchoolEngaged;
    return acc;
  }, {});

  const years = Object.keys(annualTotals)
    .map((year) => Number(year))
    .filter((year) => Number.isFinite(year));
  const minYear = years.length > 0 ? Math.min(...years) : curQ.y;
  const maxYear = years.length > 0 ? Math.max(...years) : curQ.y;

  const annualData = Array.from({ length: maxYear - minYear + 1 }, (_, idx) => minYear + idx).map((year) => ({
    year,
    yearLabel: String(year),
    total: annualTotals[year] ?? 0,
  }));

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  return (
    <CardShell title="High School Students Engaged" subtitle={cur?.quarter ?? 'No data'} statusLabel={status.label} statusColor={status.color}>
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'This Quarter',
              labelTooltip: `Unique high school students engaged this quarter. Goal at scale: ${goalPerYear}/year (~${goalPerQuarter}/quarter).`,
              value: (
                <Text size="lg" weight={800}>
                  {cur?.highSchoolEngaged ?? '—'}
                </Text>
              ),
              sublabel: qoqText,
            },
            {
              label: 'Engaged (YTD)',
              labelTooltip: `Unique high school students engaged calendar year to date. Goal: ${goalPerYear}/year.`,
              value: (
                <Text size="lg" weight={800}>
                  {engagedYtd}
                </Text>
              ),
              sublabel: (
                <Stack spacing={0}>
                  <Text size="xs" color="dimmed">
                    {yoyText}
                  </Text>
                  <Text size="xs" color="dimmed">
                    Goal {goalPerYear}/year
                  </Text>
                </Stack>
              ),
            },
          ]}
        />

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
          Annual High School Engagement
        </Text>

        <Box sx={{ height: 170 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={annualData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.18} vertical={false} />
              <XAxis dataKey="yearLabel" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
              <YAxis orientation="right" tick={{ fontSize: 11 }} allowDecimals={false} axisLine={false} tickLine={false} width={44} tickMargin={8} />
              <ReferenceLine y={goalPerYear} stroke="#fa5252" strokeDasharray="4 4" ifOverflow="extendDomain" />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const v = payload[0]?.value;
                  return (
                    <div style={tooltipShellStyle as any}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
                      <div style={{ fontWeight: 900, fontSize: 14, lineHeight: 1.2 }}>
                        {typeof v === 'number' ? v : '—'} Students
                      </div>
                    </div>
                  );
                }}
              />
              <Bar
                dataKey="total"
                name="Engaged"
                fill={theme.colors.blue[6]}
                  barSize={18}
                radius={[6, 6, 0, 0]}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Stack>
    </CardShell>
  );
}
